import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { formatIsoDate, subDays } from '../../../../Helpers/dateFormatter';
import { MEASUREMENT_TYPES } from '../../../Measurements/consts';
import ChangeSettingsMutation from './ChangeSystemOfMeasurementsMutation';
import HydationDayQuery from '../../../Hydration/FetchHydrationDay/HydrationDayQuery';
import DietSettingsMeasurementsComponentQuery from '../../../Measurements/DietSettingsMeasurementsComponentQuery';
import MeasurementHistoryQuery from '../../../Measurements/MeasurementHistory/MeasurementHistoryQuery';

const RATIO = 28.4130625;

const todayDate = formatIsoDate(new Date());
const periodEnd = new Date();
const periodLength = 31;

const optimisticResponse = {
  me: {
    setting: {
      code: 200,
      __typename: 'BasicMutationSuccess',
    },
    __typename: 'MeMutation',
  },
};

const convertToOz = (value) => {
  return Math.round(value * (1 / RATIO));
};

const convertToMl = (value) => {
  return Math.round((value * RATIO) / 50) * 50;
};

const ChangeSystemOfMeasurementsConnectorApollo = ({
  children,
  rawQueryResult,
  dietSettingsQuery,
}) => {
  const [mutate, { loading }] = useMutation(ChangeSettingsMutation, {
    refetchQueries: [
      {
        query: dietSettingsQuery,
      },
      {
        query: DietSettingsMeasurementsComponentQuery,
      },
      {
        query: HydationDayQuery,
        variables: { date: todayDate },
      },
      {
        query: MeasurementHistoryQuery,
        variables: {
          datePeriod: {
            start: formatIsoDate(subDays(periodEnd, periodLength - 1)),
            end: formatIsoDate(periodEnd),
          },
          [MEASUREMENT_TYPES.WEIGHT]: true,
        },
      },
    ],
  });

  const changeSettings = async (systemOfMeasures) => {
    await mutate({
      variables: {
        request: {
          systemOfMeasures,
        },
      },
      optimisticResponse,
      update: (cache) => {
        cache.modify({
          id: cache.identify(rawQueryResult.me),
          fields: {
            systemOfMeasures() {
              return systemOfMeasures;
            },
            hydrationSettings(existingHydrationSettings) {
              return {
                ...existingHydrationSettings,
                defaultDailyGoal: {
                  ...existingHydrationSettings.defaultDailyGoal,
                  value:
                    systemOfMeasures === 'imperial'
                      ? convertToOz(existingHydrationSettings.defaultDailyGoal.value)
                      : convertToMl(existingHydrationSettings.defaultDailyGoal.value),
                  unit: systemOfMeasures === 'imperial' ? 'oz' : 'ml',
                },
              };
            },
          },
        });
      },
    });
  };

  const result = {
    changeSettings,
    loading,
  };
  return children(result);
};

ChangeSystemOfMeasurementsConnectorApollo.propTypes = {
  children: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  rawQueryResult: PropTypes.object.isRequired,
};

export default ChangeSystemOfMeasurementsConnectorApollo;
